import { Button, Dimmer, Form, Icon, List, Loader, Message, Modal, ModalProps } from "semantic-ui-react";
import React, { useCallback, useEffect, useState } from "react";

import config from "../config";
import { useHistory } from "react-router-dom";
import useSproc from "../hooks/useSproc";

const CustomModal: React.FC<ModalProps> = (props) => (
    <Modal
        open={true}
        closeOnDimmerClick={false}
        closeOnDocumentClick={false}
        closeOnEscape={false}
        closeOnPortalMouseLeave={false}
        closeOnTriggerBlur={false}
        closeOnTriggerClick={false}
        closeOnTriggerMouseLeave={false}
        {...props}
    />
);

const MFAOptions = () => (
    <List bulleted>
        <List.Item>
            <b>Authenticator App</b>: use a mobile app like Microsoft Authenticator
        </List.Item>
        <List.Item>
            <b>Text Message</b>: receive a verification code via text
        </List.Item>
        <List.Item>
            <b>Phone Call</b>: receive a verification code through a phone call
        </List.Item>
    </List>
);

const TermsConditionsMFA: React.FC = () => {
    const history = useHistory();
    const [loading, setLoading] = useState<boolean>(false);
    const [termsError, setTermsError] = useState<string>();
    const [termsSuccess, setTermsSuccess] = useState<boolean>(false);
    const [agreeToTerms, setAgreeToTerms] = useState<boolean>(false);
    const [modalStep, setModalStep] = useState<"tc" | "mfa-prompt" | "mfa-enabled" | "mfa-reminder" | undefined>();
    const sproc = useSproc();

    const mfaCheck = useCallback(async () => {
        try {
            const [mfaData] = await sproc("MFACheck");
            const { IsMFAEnabled, ReminderDue } = mfaData;
            if (!IsMFAEnabled && ReminderDue) {
                setModalStep("mfa-prompt");
                return true;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    }, [sproc]);

    useEffect(() => {
        (async () => {
            try {
                setLoading(true);
                await sproc("CheckTermsAndConditions");
                setTermsSuccess(true);
                await mfaCheck();
            } catch (err) {
                console.log(err);
                if (err instanceof Error) {
                    setModalStep("tc");
                }
            } finally {
                setLoading(false);
            }
        })();
    }, [mfaCheck, sproc]);

    const onSubmitTerms = async () => {
        try {
            setLoading(true);
            await sproc("InsertTermsAndConditions", { ip: "IPADDRESS" });
            setTermsSuccess(true);
            const showMFAPrompt = await mfaCheck();
            if (!showMFAPrompt) {
                setModalStep(undefined);
            }
        } catch (err) {
            console.log(err);
            if (err instanceof Error) {
                setTermsError("Error saving agreement");
            }
        } finally {
            setLoading(false);
        }
    };

    const onSubmitMFA = async (Enable = false) => {
        setLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        try {
            await sproc("MFAEnable", { Enable });
            if (Enable) {
                setModalStep("mfa-enabled");
            } else {
                setModalStep("mfa-reminder");
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            {modalStep === "tc" && (
                <CustomModal>
                    <Modal.Header content="Updates" />
                    <Modal.Content>
                        <Form onSubmit={onSubmitTerms} error={!!termsError} success={termsSuccess} loading={loading}>
                            <Message error content={termsError} icon="exclamation triangle" onDismiss={() => setTermsError(undefined)} />
                            <Message success content="Online Access Agreement was saved!" icon="check" />
                            <Message
                                content="The online access agreement and/or the privacy policy have been updated. Complete the following to continue."
                                icon="info circle"
                                info
                            />
                            <div style={{ textAlign: "center", marginTop: "28px" }}>
                                <Form.Checkbox
                                    checked={agreeToTerms}
                                    onChange={(e, { checked }) => setAgreeToTerms(!!checked)}
                                    styles={{ marginBottom: "20px" }}
                                    label={
                                        <label>
                                            I have read and agree to the{" "}
                                            <a href={config.termsAndConditionsUrl} target="_blank" rel="noreferrer">
                                                Online Access Agreement.
                                            </a>{" "}
                                            I acknowledge the{" "}
                                            <a href={config.privacyPolicyUrl} target="_blank" rel="noreferrer">
                                                Privacy Policy.
                                            </a>
                                        </label>
                                    }
                                />
                            </div>
                            <div style={{ textAlign: "center", marginTop: "28px" }}>
                                <Form.Button
                                    primary
                                    type="submit"
                                    icon="check"
                                    content="I Agree"
                                    styles={{ marginTop: "100px" }}
                                    disabled={!agreeToTerms || termsSuccess || !!termsError}
                                />
                            </div>
                        </Form>
                    </Modal.Content>
                </CustomModal>
            )}

            {modalStep === "mfa-prompt" && (
                <CustomModal>
                    <Modal.Header content="Multi-Factor Authentication" />
                    <Modal.Content>
                        <Dimmer active={loading} inverted>
                            <Loader />
                        </Dimmer>
                        <p>We are committed to ensuring the highest level of security for your account.</p>
                        <p>For enhanced security, we recommend enabling Multi-Factor Authentication (MFA).</p>
                        <p>
                            <b>What is MFA?</b>
                        </p>
                        <p>
                            MFA adds an extra layer of protection to your account by requiring a second form of verification in addition to
                            your password. This helps prevent unauthorized access and keeps your information safe.
                        </p>

                        <p>The following options are available for your second form of verification:</p>

                        <MFAOptions />

                        <p>Would you like to enable MFA now?</p>
                    </Modal.Content>
                    <Modal.Actions style={{ display: "flex", justifyContent: "center" }}>
                        <Button
                            content="Enable now"
                            disabled={loading}
                            icon="shield"
                            onClick={() => onSubmitMFA(true)}
                            primary
                            style={{ minWidth: "200px" }}
                        />
                        <Button
                            basic
                            color="black"
                            content="Remind me in 30 days"
                            disabled={loading}
                            icon="clock"
                            onClick={() => onSubmitMFA()}
                            style={{ minWidth: "200px" }}
                        />
                    </Modal.Actions>
                </CustomModal>
            )}

            {(modalStep === "mfa-enabled" || modalStep === "mfa-reminder") && (
                <CustomModal size="tiny">
                    {modalStep === "mfa-enabled" && (
                        <>
                            <Modal.Header>
                                <span style={{ display: "flex", alignItems: "center" }}>
                                    <Icon name="check circle" color="green" size="large" style={{ marginRight: 12 }} />
                                    Multi-Factor Authentication Enabled
                                </span>
                            </Modal.Header>
                            <Modal.Content>
                                <p>Thank you for enabling Multi-Factor Authentication (MFA).</p>
                                <p>Your account is now better protected against unauthorized access.</p>
                                <p>
                                    <b>What happens next:</b>
                                </p>
                                <List>
                                    <List.Item>
                                        After you click <b>Continue</b> below, you will be returned to the login screen. Sign in again using
                                        your usual email address and password.
                                    </List.Item>
                                    <List.Item>
                                        You will then be guided through a short, one-time setup to register your second form of
                                        verification. Please have your mobile phone nearby.
                                    </List.Item>
                                    <List.Item>
                                        Choose the option that works best for you, and follow the on-screen instructions:
                                        <MFAOptions />
                                    </List.Item>
                                    <List.Item>
                                        From that point, each time you sign in, you will be asked to confirm using the method you chose.
                                    </List.Item>
                                </List>
                                <p>
                                    This setup only needs to be done once. If you get stuck at any point, please contact our support team
                                    for help.
                                </p>
                            </Modal.Content>
                            <Modal.Actions style={{ display: "flex", justifyContent: "center" }}>
                                <Button
                                    content="Continue to Login and MFA Setup"
                                    icon="check"
                                    onClick={() => {
                                        setModalStep(undefined);
                                        history.push("/logout");
                                    }}
                                    primary
                                />
                            </Modal.Actions>
                        </>
                    )}

                    {modalStep === "mfa-reminder" && (
                        <>
                            <Modal.Header>
                                <span style={{ display: "flex", alignItems: "center" }}>
                                    <Icon name="info circle" color="blue" size="large" style={{ marginRight: 12 }} />
                                    Multi-Factor Authentication Reminder
                                </span>
                            </Modal.Header>
                            <Modal.Content>
                                <p>You have chosen to postpone enabling Multi-Factor Authentication (MFA).</p>
                                <p>We will remind you again in 30 days.</p>
                            </Modal.Content>
                            <Modal.Actions style={{ display: "flex", justifyContent: "center" }}>
                                <Button content="Dismiss" icon="check" onClick={() => setModalStep(undefined)} primary />
                            </Modal.Actions>
                        </>
                    )}
                </CustomModal>
            )}
        </>
    );
};

export default TermsConditionsMFA;
